/**
 * AddDeviceForm
 *
 * Presentational body of the "Add Device" flow, extracted from DevicesPage.
 * Consumes useAddDeviceFlow internally so it can be dropped into the page's
 * Add Device modal or the onboarding wizard's connect step.
 *
 * The JSX is a faithful move of the original DevicesPage add-modal body.
 */
import React from 'react';
import {
  Stack,
  Select,
  TextInput,
  Alert,
  Button,
  Group,
  Text,
  Checkbox,
  Badge,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconX, IconInfoCircle, IconLock } from '@tabler/icons-react';
import { DeviceConfig, DeviceType } from '../../types/devices';
import { DEVICE_TYPE_OPTIONS } from './constants';
import { CredentialFields } from './credentialFields';
import { useAddDeviceFlow, AddDeviceDuplicatePayload } from './useAddDeviceFlow';

export interface AddDeviceFormProps {
  tankId: string;
  existingDevices?: DeviceConfig[];
  onSuccess: () => void | Promise<void>;
  onDuplicate?: (existing: DeviceConfig[], payload: AddDeviceDuplicatePayload) => void;
  /** Overrides the default "Add Device" submit label (bulk labels stay dynamic). */
  submitLabel?: string;
  /** When provided, renders a Cancel button that invokes this callback. */
  onCancel?: () => void;
  /** Pre-selects the device type (e.g. the onboarding connect step). */
  initialDeviceType?: DeviceType | null;
}

export const AddDeviceForm: React.FC<AddDeviceFormProps> = ({
  tankId,
  existingDevices,
  onSuccess,
  onDuplicate,
  submitLabel,
  onCancel,
  initialDeviceType,
}) => {
  const flow = useAddDeviceFlow({
    tankId,
    existingDevices,
    onSuccess,
    onDuplicate,
    initialDeviceType,
  });
  const {
    deviceType,
    displayName,
    username,
    email,
    password,
    enableWaterTests,
    enableMaintenance,
    isSubmitting,
    formError,
    isTesting,
    testBeforeSaveResult,
    availableDevices,
    selectedDeviceIds,
    selectedAquaWizDevice,
    kamoerDevices,
    selectedKamoerDeviceIds,
    kamoerProviderAccountId,
    setDisplayName,
    setUsername,
    setEmail,
    setPassword,
    setEnableWaterTests,
    setEnableMaintenance,
    setFormError,
    setSelectedDeviceIds,
    setSelectedAquaWizDevice,
    setSelectedKamoerDeviceIds,
    handleDeviceTypeChange,
    handleTestBeforeSave,
    handleSubmit,
  } = flow;

  /**
   * Submit label for the two bulk (discover-then-select) device types; null for
   * every other type, which falls back to `submitLabel ?? 'Add Device'`.
   *
   * Before discovery runs — and after the user unticks everything — the count is
   * 0 and the button is disabled. "Add 0 Devices" reads like a broken action, so
   * the zero case shows the neutral "Add Devices" instead. Counts of 1+ keep the
   * exact "Add N Device(s)" wording that confirms what is about to happen.
   */
  const bulkSelectedCount =
    deviceType === 'focustronic'
      ? selectedDeviceIds.size
      : deviceType === 'kamoer_kh'
      ? selectedKamoerDeviceIds.size
      : null;
  const bulkSubmitLabel =
    bulkSelectedCount === null
      ? null
      : bulkSelectedCount === 0
      ? 'Add Devices'
      : `Add ${bulkSelectedCount} Device${bulkSelectedCount !== 1 ? 's' : ''}`;

  return (
    <Stack gap="md">
      {formError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          onClose={() => setFormError(null)}
          withCloseButton
        >
          {formError}
        </Alert>
      )}

      <Select
        label="Device Type"
        placeholder="Select device type"
        required
        data={DEVICE_TYPE_OPTIONS}
        value={deviceType}
        onChange={handleDeviceTypeChange}
        disabled={isSubmitting}
      />

      {/* Display Name - not shown for Focustronic or Kamoer */}
      {deviceType && deviceType !== 'focustronic' && deviceType !== 'kamoer_kh' && (
        <TextInput
          label="Display Name"
          placeholder="Optional custom name for this device"
          value={displayName}
          onChange={(e) => setDisplayName(e.currentTarget.value)}
          disabled={isSubmitting}
        />
      )}

      {/* Focustronic: How This Works */}
      {deviceType === 'focustronic' && (
        <Alert color="blue" icon={<IconInfoCircle size={16} />}>
          <Text size="sm" fw={500} mb="xs">How this works:</Text>
          <Text size="sm" component="ol" style={{ paddingLeft: '1.2rem', margin: 0 }}>
            <li>Enter your Focustronic account credentials</li>
            <li>We'll securely connect to discover your devices</li>
            <li>Choose which devices to add to this tank</li>
          </Text>
        </Alert>
      )}

      {/* Kamoer: How This Works */}
      {deviceType === 'kamoer_kh' && (
        <Alert color="blue" icon={<IconInfoCircle size={16} />}>
          <Text size="sm" fw={500} mb="xs">How this works:</Text>
          <Text size="sm" component="ol" style={{ paddingLeft: '1.2rem', margin: 0 }}>
            <li>Enter your Kamoer account credentials</li>
            <li>We'll securely connect to discover your devices</li>
            <li>Choose which devices to add to this tank</li>
          </Text>
        </Alert>
      )}

      {/* Focustronic: Security Message (before credentials) */}
      {deviceType === 'focustronic' && (
        <Alert color="green" icon={<IconLock size={16} />}>
          <Text size="sm">
            Your credentials are encrypted and secure. We use them only
            to fetch water test data from Focustronic on your behalf. No one else can access
            your account.
          </Text>
        </Alert>
      )}

      {/* Kamoer: Security Message */}
      {deviceType === 'kamoer_kh' && (
        <Alert color="green" icon={<IconLock size={16} />}>
          <Text size="sm">
            Your credentials are encrypted and secure. We use them only
            to fetch data from Kamoer on your behalf. No one else can access
            your account.
          </Text>
        </Alert>
      )}

      {deviceType && (
        <CredentialFields
          type={deviceType}
          username={username}
          email={email}
          password={password}
          enableWaterTests={enableWaterTests}
          enableMaintenance={enableMaintenance}
          disabled={isSubmitting}
          onUsernameChange={setUsername}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onEnableWaterTestsChange={setEnableWaterTests}
          onEnableMaintenanceChange={setEnableMaintenance}
        />
      )}

      <Button
        variant="outline"
        onClick={handleTestBeforeSave}
        loading={isTesting}
        disabled={!deviceType || isSubmitting}
        fullWidth
      >
        {(deviceType === 'focustronic' || deviceType === 'aquawiz' || deviceType === 'kamoer_kh') ? 'Discover My Devices' : 'Test Connection'}
      </Button>

      {testBeforeSaveResult && (
        <Alert
          color={testBeforeSaveResult.success ? 'green' : 'red'}
          icon={testBeforeSaveResult.success ? <IconCheck size={16} /> : <IconX size={16} />}
        >
          {(deviceType === 'focustronic' || deviceType === 'aquawiz' || deviceType === 'kamoer_kh') && testBeforeSaveResult.success && (availableDevices.length > 0 || kamoerDevices.length > 0)
            ? (() => {
                if (deviceType === 'kamoer_kh') return `Found ${kamoerDevices.length} Kamoer device${kamoerDevices.length !== 1 ? 's' : ''}`;
                return `Found ${availableDevices.length} ${deviceType === 'focustronic' ? 'Focustronic' : 'AquaWiz'} device${availableDevices.length !== 1 ? 's' : ''}`;
              })()
            : testBeforeSaveResult.message}
        </Alert>
      )}

      {/* Focustronic device selection */}
      {deviceType === 'focustronic' && availableDevices.length > 0 && (
        <Stack gap="sm">
          <Text size="sm" fw={500}>Select devices to add to this tank:</Text>
          {availableDevices.map((device) => (
            <Checkbox
              key={device.id}
              label={
                <Group gap="xs">
                  <Text size="sm">
                    {device.type.charAt(0).toUpperCase() + device.type.slice(1)} - {device.friendly_name}
                  </Text>
                  <Badge size="xs" color={device.is_active ? 'green' : 'gray'}>
                    {device.is_active ? 'Online' : 'Offline'}
                  </Badge>
                </Group>
              }
              checked={selectedDeviceIds.has(device.id as number)}
              onChange={(e) => {
                const newSelected = new Set(selectedDeviceIds);
                if (e.currentTarget.checked) {
                  newSelected.add(device.id as number);
                } else {
                  newSelected.delete(device.id as number);
                }
                setSelectedDeviceIds(newSelected);
              }}
            />
          ))}
        </Stack>
      )}

      {/* AquaWiz device selection (single select) */}
      {deviceType === 'aquawiz' && availableDevices.length > 0 && (
        <Stack gap="sm">
          <Text size="sm" fw={500}>Select your AquaWiz device:</Text>
          {availableDevices.map((device) => (
            <Checkbox
              key={device.id}
              label={
                <Group gap="xs">
                  <Text size="sm">{device.friendly_name}</Text>
                  <Badge size="xs" color="blue">KH Monitor</Badge>
                </Group>
              }
              checked={selectedAquaWizDevice === String(device.id)}
              onChange={(e) => {
                setSelectedAquaWizDevice(e.currentTarget.checked ? String(device.id) : null);
              }}
            />
          ))}
        </Stack>
      )}

      {/* Kamoer device selection */}
      {deviceType === 'kamoer_kh' && kamoerDevices.length > 0 && (
        <Stack gap="sm">
          <Text size="sm" fw={500}>Select Kamoer devices to add:</Text>
          {kamoerDevices.map((device) => {
            const isUnsupported = device.device_type === 'unknown' || !device.is_supported;
            return (
              <Checkbox
                key={device.iotId}
                disabled={isUnsupported}
                label={
                  <Group gap="xs">
                    <Text size="sm">
                      {device.productName || device.productModel || device.deviceName}
                    </Text>
                    <Badge size="xs" color={isUnsupported ? 'gray' : 'teal'} variant="light">
                      {device.device_type === 'kamoer_kh' ? 'KH Carer'
                        : device.device_type === 'kamoer_stp' ? 'STP Connect'
                        : device.device_type === 'kamoer_p4' ? 'P4 Pro'
                        : 'Unsupported'}
                    </Badge>
                  </Group>
                }
                checked={selectedKamoerDeviceIds.has(device.iotId)}
                onChange={(e) => {
                  const newSelected = new Set(selectedKamoerDeviceIds);
                  if (e.currentTarget.checked) {
                    newSelected.add(device.iotId);
                  } else {
                    newSelected.delete(device.iotId);
                  }
                  setSelectedKamoerDeviceIds(newSelected);
                }}
              />
            );
          })}
          {kamoerDevices.some(d => d.device_type === 'unknown' || !d.is_supported) && (
            <Text size="xs" c="dimmed">
              Unsupported device models cannot be added.
            </Text>
          )}
        </Stack>
      )}

      {/* AquaWiz: No devices found warning */}
      {deviceType === 'aquawiz' && testBeforeSaveResult?.success && availableDevices.length === 0 && (
        <Alert color="orange" icon={<IconAlertCircle size={16} />}>
          No AquaWiz devices found for this account. Please check your AquaWiz app.
        </Alert>
      )}

      {/* Non-Focustronic/AquaWiz/Kamoer: Security Message (for other devices, show at bottom) */}
      {deviceType && deviceType !== 'focustronic' && deviceType !== 'aquawiz' && deviceType !== 'kamoer_kh' && (
        <Alert color="green" icon={<IconLock size={16} />}>
          <Text size="sm">
            Your credentials are encrypted and secure. We use them only
            to fetch data from your devices on your behalf. No one else can access your account.
          </Text>
        </Alert>
      )}

      <Group justify="flex-end" gap="xs">
        {onCancel && (
          <Button
            variant="subtle"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={
            isSubmitting ||
            (deviceType === 'focustronic' && selectedDeviceIds.size === 0) ||
            (deviceType === 'aquawiz' && !selectedAquaWizDevice) ||
            (deviceType === 'kamoer_kh' && (selectedKamoerDeviceIds.size === 0 || !kamoerProviderAccountId))
          }
        >
          {bulkSubmitLabel ?? submitLabel ?? 'Add Device'}
        </Button>
      </Group>
    </Stack>
  );
};
